import {apiClient, getHeader, getOrganizationId, handleError, handleResponse} from '@/helpers/api'
import store from '@/store'
import {jwtDecode} from '@/helpers/jwt'

// EXPORTS
export const organizationService = {
  getMyOrganizations,
  getOrganizationById,
  create,
  rename,
  deleteOrganization,
  approveInvitation,
  join,
  sendInvitation,
  addUser,
  toggleAdminStatus,
  status,
  invitedPeople,
  getBillingData,
  setBillingData,
  existsUser,
  deleteUser,
  deleteInvitation,
  lockUserSettings,
  setOrgaMerge,
  downloadCSVTemplate,
  validateCSV,
  importCSV,
  exportCSV,
  updateOrganizationPermissions,
  changeSelectedOrganization,
  clientCSVDownload,
  getOrganizationNameById
}

// API CALLS
async function getMyOrganizations () {
  return apiClient
  .get(`/organization/my`)
  .then(handleResponse)
  .catch(handleError)
}

async function getOrganizationById (id) {
  return apiClient
  .get(`/organization?organizationId=${id}`)
  .then(handleResponse)
  .catch(handleError)
}

async function create(payload) {
  return apiClient
  .post('/organization/create', payload)
  .then(handleResponse)
  .catch(handleError)
}

async function rename(organization) {
  return apiClient
  .put(`/organization/${organization.id}/rename`,
    JSON.stringify(organization.name),
    getHeader())
  .then(handleResponse)
  .catch(handleError)
}

async function deleteOrganization(organizationId) {
  return apiClient
  .delete(`/organization/${organizationId}`)
  .then(handleResponse)
}

async function approveInvitation(payload) {
  const invitationTokenDecoded = payload.token && jwtDecode(payload.token)
  return apiClient
  .post(`/organization/${invitationTokenDecoded.id}/approveInvitation`,
    payload.data,
    {
      headers: {Authorization: 'bearer ' + payload.token}
    })
  .then(handleResponse)
  .catch(handleError)
}

async function join(payload) {
  const invitationTokenDecoded = payload.token && jwtDecode(payload.token)
  return apiClient
  .post(`/organization/${invitationTokenDecoded.id}/join`,
    payload.data, {
      headers: {Authorization: 'bearer ' + payload.token}
    })
  .then(handleResponse)
  .catch(handleError)
}

async function sendInvitation(payload, alternativeOrganizationId = null) {
  const organizationId = alternativeOrganizationId !== null ? alternativeOrganizationId : (store.getters.selectedOrganization.id || null)
  return apiClient
  .post(`/organization/${organizationId}/sendInvitation`,
    payload)
  .then(handleResponse)
  .catch(handleError)
}

async function addUser(payload, organizationId = null) {
  const orgaId = organizationId || getOrganizationId()
  return apiClient
  .post(`/organization/${orgaId}/addUser`,
    payload)
  .then(handleResponse)
  .catch(handleError)
}

async function toggleAdminStatus(user, organization = null) {
  const organizationId = (organization !== null && 'id' in organization) ? organization.id : (store.getters.selectedOrganization.id || null)
  return apiClient
  .put(`/organization/${organizationId}/admin/${user.id}`,
    JSON.stringify(!user.isAdmin),
    getHeader())
  .then(handleResponse)
  .catch(handleError)
}

async function status(payload) {
  const invitationTokenDecoded = payload.token && jwtDecode(payload.token)
  return apiClient
  .get(`/organization/${invitationTokenDecoded.id}/invitation/status`,
    {
      headers: {
        Authorization: 'bearer ' + payload.token
      }
    })
  .then(handleResponse)
  .catch(handleError)
}

async function invitedPeople(organizationId = null) {
  const orgaId = organizationId || getOrganizationId()
  return apiClient
  .get(`/organization/${orgaId}/invitedPeople`)
  .then((response) => {
    return response.data && response.data
  })
  .catch(handleError)
}

async function getBillingData(organizationId) {
  return apiClient
  .get(`/organization/${organizationId}/billing-address`)
  .then(handleResponse)
  .catch(handleError)
}

async function setBillingData(organizationId, payload) {
  return apiClient
  .post(`/organization/${organizationId}/billing-address`, payload)
  .then(handleResponse)
  .catch(handleError)
}

async function downloadCSVTemplate() {
  return apiClient
  .get('/organization/csv-template',
    getHeader())
  .then(handleResponse)
  .catch(handleError)
}

async function validateCSV(csv) {
  return apiClient
  .post('/organization/csv-validation',
    csv,
    getHeader({}, 'multipart/form-data'))
  .then(handleResponse)
  .catch(handleError)
}

async function importCSV(payload) {
  return apiClient
  .post(`/organization/${getOrganizationId()}/csv-import`,
    payload,
    getHeader({}, 'multipart/form-data'))
  .then(handleResponse)
  .catch(handleError)
}

async function exportCSV(organizationId) {
  return apiClient
  .get(`/organization/${organizationId}/csv-export`,
    getHeader())
  .then(handleResponse)
  .catch(handleError)
}


async function existsUser(email) {
  return apiClient
  .get(`/organization/${getOrganizationId()}/existsUser`,
    getHeader({params: {email: email}}))
  .then(handleResponse)
  .catch(handleError)
}

async function deleteUser(userId, organizationId = null) {
  const orgaIdUrlParam = organizationId || getOrganizationId()
  return apiClient
  .delete(`/organization/${orgaIdUrlParam}/deleteUser/${userId}`)
  .then(handleResponse)
}

async function deleteInvitation(userId, organizationId = null) {
  const orgaId = organizationId || getOrganizationId()
  return apiClient
  .delete(`/organization/${orgaId}/deleteInvitation/${userId}`)
  .then(handleResponse)
}

async function lockUserSettings(payload) {
  return apiClient
  .put(`/organization/${getOrganizationId()}/lockUserSettings`,
    JSON.stringify(payload),
    getHeader())
  .then(handleResponse)
  .catch(handleError)
}

async function setOrgaMerge(payload) {
  return apiClient
  .put(`/organization/${getOrganizationId()}/setOrgaMerge`,
    JSON.stringify(payload),
    getHeader())
  .then(handleResponse)
  .catch(handleError)
}

async function updateOrganizationPermissions(payload, organization) {
  const organizationId = 'id' in organization && organization.id || store.getters.selectedOrganization.id || null
  return apiClient
  .put(`/organization/${organizationId}/permissions`, payload)
  .then(handleResponse)
  .catch(handleError)
}

async function changeSelectedOrganization(organization) {
  return apiClient
  .get(`/organization/change?organizationId=${organization?.id}`)
  .then(handleResponse)
  .catch(handleError)
}

// HELPERS
async function clientCSVDownload(response, documentTitle = 'download.csv') {
  let base64String = response.data.bytes // -> base64 string
  let byteCharacters = atob(base64String)
  let byteNumbers = new Array(byteCharacters.length)
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i)
  }
  let byteArray = new Uint8Array(byteNumbers)
  const url = window.URL.createObjectURL(new Blob([byteArray], {type: 'text/csv;charset=utf-8'}))
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', documentTitle)
  document.body.appendChild(link)
  link.click()
}

function getOrganizationNameById(organizationId) {
  return store?.getters?.organizations?.find((organization) => organization?.id === organizationId)?.name
}
